import * as React from "react"

const MOBILE_BREAKPOINT = 768
const DESKTOP_BREAKPOINT = 1280

/**
 * The single sidebar breakpoint classification, shared by every responsive
 * sidebar decision:
 *   - `mobile`  (<768px)        → sheet overlay
 *   - `tablet`  (768–1279px)    → rail by default
 *   - `desktop` (≥1280px)       → expanded by default
 *
 * `useIsMobile`/`useIsTablet` are thin wrappers so existing call sites (e.g. the
 * Orders kanban default) keep working off the same source of truth.
 */
export type SidebarBreakpoint = "mobile" | "tablet" | "desktop"

export function useSidebarBreakpoint(): SidebarBreakpoint {
  return React.useSyncExternalStore(
    (onStoreChange) => {
      const mqls = [
        window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`),
        window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`),
      ]
      mqls.forEach((mql) => mql.addEventListener("change", onStoreChange))
      return () =>
        mqls.forEach((mql) => mql.removeEventListener("change", onStoreChange))
    },
    () => {
      const width = window.innerWidth
      if (width < MOBILE_BREAKPOINT) return "mobile"
      if (width < DESKTOP_BREAKPOINT) return "tablet"
      return "desktop"
    },
    () => "desktop",
  )
}

export function useIsMobile() {
  return useSidebarBreakpoint() === "mobile"
}

/**
 * Tablet range per the design system: 768px–1279px (e.g. a Galaxy Tab S9 in
 * landscape at 1138px). Used to default the Orders page to its kanban board.
 */
export function useIsTablet() {
  return useSidebarBreakpoint() === "tablet"
}
